import React, { memo, useCallback, useRef, useEffect, useState } from 'react';
import { cn } from '../lib/utils';

// Piano key configuration for 61 keys (C2 to C7 - 5 octaves)
const PIANO_KEYS = [
  // Octave 2
  { note: 'C2', isBlack: false, hasBlackRight: true },
  { note: 'D2', isBlack: false, hasBlackLeft: true, hasBlackRight: true },
  { note: 'E2', isBlack: false, hasBlackLeft: true },
  { note: 'F2', isBlack: false, hasBlackRight: true },
  { note: 'G2', isBlack: false, hasBlackLeft: true, hasBlackRight: true },
  { note: 'A2', isBlack: false, hasBlackLeft: true, hasBlackRight: true },
  { note: 'B2', isBlack: false, hasBlackLeft: true },
  // Octave 3
  { note: 'C3', isBlack: false, hasBlackRight: true },
  { note: 'D3', isBlack: false, hasBlackLeft: true, hasBlackRight: true },
  { note: 'E3', isBlack: false, hasBlackLeft: true },
  { note: 'F3', isBlack: false, hasBlackRight: true },
  { note: 'G3', isBlack: false, hasBlackLeft: true, hasBlackRight: true },
  { note: 'A3', isBlack: false, hasBlackLeft: true, hasBlackRight: true },
  { note: 'B3', isBlack: false, hasBlackLeft: true },
  // Octave 4 (Middle C)
  { note: 'C4', isBlack: false, isMiddleC: true, hasBlackRight: true },
  { note: 'D4', isBlack: false, hasBlackLeft: true, hasBlackRight: true },
  { note: 'E4', isBlack: false, hasBlackLeft: true },
  { note: 'F4', isBlack: false, hasBlackRight: true },
  { note: 'G4', isBlack: false, hasBlackLeft: true, hasBlackRight: true },
  { note: 'A4', isBlack: false, hasBlackLeft: true, hasBlackRight: true },
  { note: 'B4', isBlack: false, hasBlackLeft: true },
  // Octave 5
  { note: 'C5', isBlack: false, hasBlackRight: true },
  { note: 'D5', isBlack: false, hasBlackLeft: true, hasBlackRight: true },
  { note: 'E5', isBlack: false, hasBlackLeft: true },
  { note: 'F5', isBlack: false, hasBlackRight: true },
  { note: 'G5', isBlack: false, hasBlackLeft: true, hasBlackRight: true },
  { note: 'A5', isBlack: false, hasBlackLeft: true, hasBlackRight: true },
  { note: 'B5', isBlack: false, hasBlackLeft: true },
  // Octave 6
  { note: 'C6', isBlack: false, hasBlackRight: true },
  { note: 'D6', isBlack: false, hasBlackLeft: true, hasBlackRight: true },
  { note: 'E6', isBlack: false, hasBlackLeft: true },
  { note: 'F6', isBlack: false, hasBlackRight: true },
  { note: 'G6', isBlack: false, hasBlackLeft: true, hasBlackRight: true },
  { note: 'A6', isBlack: false, hasBlackLeft: true, hasBlackRight: true },
  { note: 'B6', isBlack: false, hasBlackLeft: true },
  // Octave 7 (just C7 to complete 61 keys)
  { note: 'C7', isBlack: false },
];

const getBaseNote = (note) => note.replace('#', '').replace('b', '');

const VirtualPiano = memo(function VirtualPiano({ 
  onKeyPress, 
  activeKey = null, 
  feedbackKey = null,
  feedbackType = null,
  showNoteNames = false,
  availableNotes = [],
  disabled = false
}) {
  const containerRef = useRef(null);
  const scrollRef = useRef(null);
  const middleCRef = useRef(null);
  const keyRefs = useRef({});
  const [scrollState, setScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
    thumbWidth: 30,
    thumbLeft: 35,
    needsScrollbar: true
  });
  
  // Check if a note is available/playable
  const isNoteAvailable = useCallback((note) => {
    if (availableNotes.length === 0) return true;
    const keyNote = getBaseNote(note);
    return availableNotes.includes(keyNote);
  }, [availableNotes]);
  
  // Check scroll position to update edge shadows and scrollbar
  const updateScrollIndicators = useCallback(() => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      const maxScroll = scrollWidth - clientWidth;
      
      // Find the range of playable keys
      const playableKeys = PIANO_KEYS.filter(key => isNoteAvailable(key.note));
      
      let needsScrollbar = false;
      
      if (playableKeys.length > 0 && Object.keys(keyRefs.current).length > 0) {
        // Get the first and last playable key elements
        const firstPlayableNote = playableKeys[0].note;
        const lastPlayableNote = playableKeys[playableKeys.length - 1].note;
        
        const firstKeyEl = keyRefs.current[firstPlayableNote];
        const lastKeyEl = keyRefs.current[lastPlayableNote];
        
        if (firstKeyEl && lastKeyEl) {
          // Calculate the visible area bounds
          const containerRect = scrollRef.current.getBoundingClientRect();
          const firstKeyRect = firstKeyEl.getBoundingClientRect();
          const lastKeyRect = lastKeyEl.getBoundingClientRect();
          
          // Check if first playable key is visible (left edge)
          const firstKeyVisible = firstKeyRect.left >= containerRect.left - 5;
          // Check if last playable key is visible (right edge)
          const lastKeyVisible = lastKeyRect.right <= containerRect.right + 5;
          
          // Only show scrollbar if some playable keys are off-screen
          needsScrollbar = !firstKeyVisible || !lastKeyVisible;
        }
      } else {
        // Fallback: use total scroll width check
        needsScrollbar = scrollWidth > clientWidth + 5;
      }
      
      // Calculate thumb size and position
      const thumbWidth = Math.max(20, (clientWidth / scrollWidth) * 100);
      const thumbLeft = maxScroll > 0 
        ? (scrollLeft / maxScroll) * (100 - thumbWidth) 
        : 0;
      
      setScrollState({
        canScrollLeft: scrollLeft > 5,
        canScrollRight: scrollLeft < maxScroll - 5,
        thumbWidth,
        thumbLeft,
        needsScrollbar
      });
    }
  }, [isNoteAvailable]);
  
  // Center the keyboard on Middle C when component mounts
  useEffect(() => {
    if (middleCRef.current && scrollRef.current) {
      const container = scrollRef.current;
      const middleC = middleCRef.current;
      
      const containerWidth = container.clientWidth;
      const middleCOffset = middleC.offsetLeft;
      const middleCWidth = middleC.clientWidth;
      
      const scrollPosition = middleCOffset - (containerWidth / 2) + (middleCWidth / 2);
      container.scrollLeft = Math.max(0, scrollPosition);
      
      // Update indicators after centering
      setTimeout(updateScrollIndicators, 50);
    }
  }, [updateScrollIndicators]);
  
  // Listen for scroll events and resize
  useEffect(() => {
    const container = scrollRef.current;
    if (container) {
      container.addEventListener('scroll', updateScrollIndicators);
      window.addEventListener('resize', updateScrollIndicators);
      updateScrollIndicators();
      
      // Run again after a brief delay to catch any layout shifts
      const timeout = setTimeout(updateScrollIndicators, 100);
      
      return () => {
        container.removeEventListener('scroll', updateScrollIndicators);
        window.removeEventListener('resize', updateScrollIndicators);
        clearTimeout(timeout);
      };
    }
  }, [updateScrollIndicators]);
  
  const handleKeyClick = useCallback((note) => {
    if (disabled) return;
    if (!isNoteAvailable(note)) return;
    if (onKeyPress) {
      onKeyPress(note);
    }
  }, [onKeyPress, disabled, isNoteAvailable]);
  
  // Update scroll indicators when availableNotes changes
  useEffect(() => {
    // Small delay to ensure refs are populated
    const timeout = setTimeout(updateScrollIndicators, 100);
    return () => clearTimeout(timeout);
  }, [availableNotes, updateScrollIndicators]);

  return (
    <div 
      ref={containerRef}
      className="w-full h-full flex flex-col"
      role="group"
      aria-label="Virtual piano keyboard"
    >
      {/* Red/Coral band at top with shadows */}
      <div className="relative flex-shrink-0">
        {/* Shadow above the band */}
        <div 
          className="absolute inset-x-0 top-0 h-1 z-10 pointer-events-none"
          style={{
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, transparent 100%)'
          }}
        />
        {/* The coral band - 8px */}
        <div 
          className="h-2 w-full"
          style={{ backgroundColor: '#CC7A6F' }}
        />
        {/* Shadow below the band */}
        <div 
          className="absolute inset-x-0 -bottom-1 h-2 z-10 pointer-events-none"
          style={{
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, transparent 100%)'
          }}
        />
      </div>
      
      {/* Scrollable keys area - fills available space */}
      <div className="relative flex-1 bg-black/80">
        {/* Left scroll shadow indicator */}
        <div 
          className={cn(
            "absolute left-0 top-0 bottom-0 w-16 z-20 pointer-events-none transition-opacity duration-200",
            scrollState.canScrollLeft ? "opacity-100" : "opacity-0"
          )}
          style={{
            background: 'linear-gradient(to right, rgba(43,37,31,0.18) 0%, transparent 100%)'
          }}
        />
        
        {/* Right scroll shadow indicator */}
        <div 
          className={cn(
            "absolute right-0 top-0 bottom-0 w-16 z-20 pointer-events-none transition-opacity duration-200",
            scrollState.canScrollRight ? "opacity-100" : "opacity-0"
          )}
          style={{
            background: 'linear-gradient(to left, rgba(43,37,31,0.18) 0%, transparent 100%)'
          }}
        />
        
        {/* Scrollable container */}
        <div 
          ref={scrollRef}
          className="h-full overflow-x-auto piano-scroll"
          style={{ 
            WebkitOverflowScrolling: 'touch'
          }}
        >
          <div className="flex min-w-min h-full mx-auto">
            <div className="relative flex h-full">
              {PIANO_KEYS.map((key) => {
                const baseNote = getBaseNote(key.note);
                const isActive = activeKey === baseNote;
                const isFeedback = feedbackKey === baseNote;
                const isAvailable = isNoteAvailable(key.note);
                
                let feedbackClass = '';
                if (isFeedback && feedbackType === 'correct') {
                  feedbackClass = 'correct-feedback';
                } else if (isFeedback && feedbackType === 'incorrect') {
                  feedbackClass = 'incorrect-feedback';
                }
                
                return (
                  <div 
                    key={key.note} 
                    className="relative h-full"
                    ref={(el) => {
                      // Store ref for each key
                      keyRefs.current[key.note] = el;
                      // Also store middleC ref
                      if (key.isMiddleC) {
                        middleCRef.current = el;
                      }
                    }}
                  >
                    {/* White key */}
                    <button
                      onClick={() => handleKeyClick(baseNote)}
                      disabled={disabled || !isAvailable}
                      className={cn(
                        "piano-key-white relative",
                        "w-10 sm:w-12 md:w-14 h-full",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-wood-500 focus-visible:ring-inset",
                        "touch-btn",
                        isActive && "active",
                        feedbackClass,
                        key.isMiddleC && "middle-c-key",
                        !isAvailable && "opacity-30 cursor-not-allowed",
                        disabled && "cursor-not-allowed"
                      )}
                      aria-label={`Piano key ${baseNote}`}
                      aria-pressed={isActive}
                      data-testid={`piano-key-${baseNote}`}
                    >
                      {/* Note name label - positioned 16px from bottom, only show on enabled keys */}
                      {showNoteNames && isAvailable && (
                        <span 
                          className="absolute left-1/2 -translate-x-1/2 text-[13px] font-sans font-semibold select-none leading-none"
                          style={{ 
                            color: '#6B7F8C',
                            bottom: '16px'
                          }}
                        >
                          {baseNote[0]}
                        </span>
                      )}
                    </button>
                    
                    {/* Black key - proportional height */}
                    {key.hasBlackRight && (
                      <div 
                        className="piano-key-black absolute top-0 right-0 translate-x-1/2 z-10 w-6 sm:w-7 md:w-8 h-[60%] pointer-events-none"
                        aria-hidden="true"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom wood panel with scrollbar */}
      <div 
        className="h-10 relative flex-shrink-0"
        style={{ backgroundColor: '#504C48' }}
      >
        {/* Shadow at top of wood panel */}
        <div 
          className="absolute inset-x-0 top-0 h-3 pointer-events-none"
          style={{
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, transparent 100%)'
          }}
        />
        
        {/* Scrollbar track - only show if scrolling is needed */}
        {scrollState.needsScrollbar && (
          <div className="absolute inset-x-8 top-1/2 -translate-y-1/2 h-1.5 bg-black/30 rounded-full">
            {/* Scrollbar thumb */}
            <div 
              className="h-full rounded-full transition-[width,margin-left] duration-75"
              style={{
                backgroundColor: '#77716B',
                width: `${scrollState.thumbWidth}%`,
                marginLeft: `${scrollState.thumbLeft}%`
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
});

export default VirtualPiano;
